import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services/cybersecurity' },
  { label: 'About', href: '/about' },
  { label: 'Locations', href: '/locations/new-york' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-sm border-b border-slate-200'
          : 'bg-white/95 backdrop-blur-md border-b border-slate-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center whitespace-nowrap flex-shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/0da62401-e0be-45f3-bc33-e277c8c6ee07/705574f5-5d9c-49da-a4c3-693d8a7197bc_secure-logo.jpg?v=fddac8ba09e154ac3e000afc7e4c8117"
            alt="Secure2Ware Security Systems"
            className="h-10 w-auto object-contain block"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={`text-sm font-semibold transition-colors whitespace-nowrap ${
                isActive(link.href)
                  ? 'text-slate-900 border-b-2 border-orange-500 pb-0.5'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right CTAs */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:+18005551234"
            className="flex items-center gap-2 text-slate-700 text-sm font-bold hover:text-orange-600 transition-colors whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill text-orange-500" />
            </div>
            (800) 555-1234
          </a>
          <Link
            to="#contact"
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-black hover:bg-slate-800 transition-all duration-200 whitespace-nowrap"
          >
            Free Quote
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden w-9 h-9 flex items-center justify-center text-slate-700 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-4">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={`flex items-center py-3 text-sm font-semibold border-b border-slate-100 transition-colors ${
                isActive(link.href) ? 'text-orange-600' : 'text-slate-700 hover:text-slate-900'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-4 flex flex-col gap-3">
            <a
              href="tel:+18005551234"
              className="flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-100 border border-slate-200 text-slate-900 font-bold text-sm"
            >
              <i className="ri-phone-fill text-orange-500" />
              Call Now: (800) 555-1234
            </a>
            <Link
              to="#contact"
              className="block py-3 text-center rounded-xl bg-slate-900 text-white font-black text-sm"
            >
              Get Free Security Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
